import { useMemo, useState } from "react";
import useAffiliatePayments from "../../../components/hooks/useAffiliatePayments";
import { downloadCsv } from "../../../utils/util";
import PaymentsRangeFilter, {
  PaymentsRange,
  rangeLabel,
} from "./PaymentsRangeFilter";
import DSCard from "views/system-design/DSCard";
import DSCardContent from "views/system-design/DSCardContent";
import DSBox from "views/system-design/DSBox";
import DSStack from "views/system-design/DSStack";
import DSTypography from "views/system-design/DSTypography";
import DSButton from "views/system-design/DSButton";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import {
  DSTable,
  DSTableHead,
  DSTableBody,
  DSTableRow,
  DSTableCell,
} from "views/system-design/Table";

const CURRENCY = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
});

/** 'YYYY-MM' -> 'Jan 2026'. */
const formatMonth = (entitlementMonth: string) => {
  const [year, month] = entitlementMonth.split("-").map(Number);
  if (!year || !month) return entitlementMonth;
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};

function AffiliatePaymentsWidget() {
  const [range, setRange] = useState<PaymentsRange>({});

  const { data, isLoading, isError } = useAffiliatePayments(range);
  const payments = data ?? [];

  const totals = useMemo(
    () =>
      payments.reduce(
        (acc, payment) => ({
          amount: acc.amount + payment.amount,
          clocks: acc.clocks + payment.clocks,
          orders: acc.orders + payment.orders,
        }),
        { amount: 0, clocks: 0, orders: 0 },
      ),
    [payments],
  );

  const exportCsv = () => {
    downloadCsv({
      filename: `edition-payments_${rangeLabel(range)}.csv`,
      headers: [
        "Artist",
        "Collection",
        "Entitlement Month",
        "Clocks",
        "Orders",
        "Amount (USD)",
      ],
      rows: payments.map((payment) => [
        payment.artistName || payment.artistId,
        payment.collectionName || "Untitled collection",
        payment.entitlementMonth,
        payment.clocks,
        payment.orders,
        payment.amount.toFixed(2),
      ]),
    });
  };

  const numericCellSx = { textAlign: "right" as const, whiteSpace: "nowrap" };

  return (
    <DSCard
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: "20px",
      }}
    >
      <DSBox
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          pt: 2,
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <DSTypography variant="h6">Artist Edition Payments</DSTypography>
        <DSStack
          direction="row"
          alignItems="center"
          spacing={1}
          flexWrap="wrap"
          justifyContent="flex-end"
        >
          <PaymentsRangeFilter onChange={setRange} />
          <DSButton
            size="small"
            variant="outlined"
            disabled={!payments.length}
            onClick={exportCsv}
          >
            Export CSV
          </DSButton>
        </DSStack>
      </DSBox>
      <DSCardContent sx={{ flex: 1, overflowX: "auto" }}>
        {isLoading ? (
          <DSBox sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <DSCircularProgress />
          </DSBox>
        ) : isError && !data ? (
          <DSTypography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", py: 3 }}
          >
            Failed to load edition payments
          </DSTypography>
        ) : !payments.length ? (
          <DSTypography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", py: 3 }}
          >
            No edition payments in this period
          </DSTypography>
        ) : (
          <DSTable size="small">
            <DSTableHead>
              <DSTableRow>
                <DSTableCell>Artist</DSTableCell>
                <DSTableCell>Collection</DSTableCell>
                <DSTableCell>Entitlement Month</DSTableCell>
                <DSTableCell sx={numericCellSx}>Clocks</DSTableCell>
                <DSTableCell sx={numericCellSx}>Orders</DSTableCell>
                <DSTableCell sx={numericCellSx}>Amount</DSTableCell>
              </DSTableRow>
            </DSTableHead>
            <DSTableBody>
              {payments.map((payment) => (
                <DSTableRow
                  key={`${payment.collectionId}_${payment.entitlementMonth}`}
                  hover
                >
                  <DSTableCell>
                    {payment.artistName || payment.artistId}
                  </DSTableCell>
                  <DSTableCell>
                    {payment.collectionName || "Untitled collection"}
                  </DSTableCell>
                  <DSTableCell sx={{ whiteSpace: "nowrap" }}>
                    {formatMonth(payment.entitlementMonth)}
                  </DSTableCell>
                  <DSTableCell sx={numericCellSx}>{payment.clocks}</DSTableCell>
                  <DSTableCell sx={numericCellSx}>{payment.orders}</DSTableCell>
                  <DSTableCell sx={{ ...numericCellSx, fontWeight: 600 }}>
                    {CURRENCY.format(payment.amount)}
                  </DSTableCell>
                </DSTableRow>
              ))}
              <DSTableRow>
                <DSTableCell colSpan={3} sx={{ fontWeight: 700 }}>
                  Total ({payments.length}{" "}
                  {payments.length === 1 ? "row" : "rows"})
                </DSTableCell>
                <DSTableCell sx={{ ...numericCellSx, fontWeight: 700 }}>
                  {totals.clocks}
                </DSTableCell>
                <DSTableCell sx={{ ...numericCellSx, fontWeight: 700 }}>
                  {totals.orders}
                </DSTableCell>
                <DSTableCell sx={{ ...numericCellSx, fontWeight: 700 }}>
                  {CURRENCY.format(totals.amount)}
                </DSTableCell>
              </DSTableRow>
            </DSTableBody>
          </DSTable>
        )}
      </DSCardContent>
      <DSBox sx={{ px: 2, py: 1.5 }}>
        <DSTypography variant="caption" sx={{ color: "text.secondary" }}>
          One row per artist, collection and month. The edition payment is a
          per-clock rate sent by the payout cron once an order ships, so the
          entitlement month is the month it was paid.
        </DSTypography>
      </DSBox>
    </DSCard>
  );
}

export default AffiliatePaymentsWidget;
